import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { LayoutDashboard, CheckSquare, FolderKanban, Activity, Clock, BarChart3, Zap, Calendar } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { toast } from "sonner";

const availableWidgets = [
  { type: "stats", label: "Statistikk", icon: BarChart3, description: "Oversikt over kunder, investorer og prosjekter" },
  { type: "myTasks", label: "Mine Oppgaver", icon: CheckSquare, description: "Dine aktive oppgaver" },
  { type: "upcomingTasks", label: "Kommende Oppgaver", icon: Calendar, description: "Oppgaver neste 7 dager" },
  { type: "projects", label: "Prosjektoversikt", icon: FolderKanban, description: "Status på aktive prosjekter" },
  { type: "activities", label: "Nylige Aktiviteter", icon: Activity, description: "Siste aktiviteter i systemet" },
  { type: "timeTracking", label: "Tidsregistrering", icon: Clock, description: "Registrert tid denne uken" },
  { type: "investorHealth", label: "Investorhelse", icon: Activity, description: "Relasjonshelse-score for investorer" },
  { type: "integrations", label: "Integrasjoner", icon: Zap, description: "Tilgjengelige integrasjoner" },
];

const defaultWidgets = [
  { id: "stats", type: "stats", order: 0 },
  { id: "projects", type: "projects", order: 1 },
  { id: "myTasks", type: "myTasks", order: 2 },
  { id: "timeTracking", type: "timeTracking", order: 3 },
  { id: "activities", type: "activities", order: 4 },
];

export default function DashboardSettings({ user }) {
  const [selectedWidgets, setSelectedWidgets] = useState(defaultWidgets);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (user?.dashboard_config && Array.isArray(user.dashboard_config) && user.dashboard_config.length > 0) {
      setSelectedWidgets(user.dashboard_config);
    }
  }, [user]);

  const updateDashboardMutation = useMutation({
    mutationFn: (data) => base44.auth.updateMe(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });
      toast.success("Dashboard-innstillinger oppdatert");
    },
  });

  const handleToggle = (type) => {
    const exists = selectedWidgets.find(w => w.type === type);
    let updatedWidgets;
    
    if (exists) {
      updatedWidgets = selectedWidgets.filter(w => w.type !== type);
    } else {
      updatedWidgets = [...selectedWidgets, { id: type, type, order: selectedWidgets.length }];
    }
    
    setSelectedWidgets(updatedWidgets);
  };

  const handleSave = () => {
    updateDashboardMutation.mutate({
      dashboard_config: selectedWidgets,
    });
  };

  const handleReset = () => {
    setSelectedWidgets(defaultWidgets);
    updateDashboardMutation.mutate({
      dashboard_config: defaultWidgets,
    });
  };

  return (
    <Card className="p-6 bg-white border-0 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-lime-100 flex items-center justify-center">
            <LayoutDashboard className="w-5 h-5 text-zinc-900" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Dashboard-widgets</h3>
            <p className="text-sm text-slate-500">Velg hvilke widgets som skal vises på dashboardet ditt</p>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={handleReset}
          size="sm"
        >
          Tilbakestill til standard
        </Button>
      </div>

      <div className="space-y-3 mb-6">
        {availableWidgets.map((widget) => {
          const isSelected = selectedWidgets.some(w => w.type === widget.type);
          const Icon = widget.icon;
          
          return (
            <div
              key={widget.type}
              className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
              onClick={() => handleToggle(widget.type)}
            >
              <Checkbox checked={isSelected} className="mt-1" />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Icon className="w-4 h-4 text-zinc-900" />
                  <h4 className="font-medium text-slate-900">{widget.label}</h4>
                </div>
                <p className="text-sm text-slate-600">{widget.description}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between pt-6 border-t border-slate-200">
        <p className="text-sm text-slate-500">
          {selectedWidgets.length} widget{selectedWidgets.length !== 1 ? "s" : ""} valgt
        </p>
        <Button
          onClick={handleSave}
          disabled={updateDashboardMutation.isPending}
          className="bg-slate-900 hover:bg-slate-800"
        >
          {updateDashboardMutation.isPending ? "Lagrer..." : "Lagre endringer"}
        </Button>
      </div>
    </Card>
  );
}